import { Suspense, lazy } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { BackofficeErrorBoundary } from './components/backoffice/BackofficeErrorBoundary';
import { ThemeProvider } from './components/providers/ThemeProvider';
import { PublicErrorBoundary } from './components/public/PublicErrorBoundary';

// Try a different lazy loading approach
const BackofficeLayout = lazy(() => import('./components/backoffice/BackofficeLayout'));
const PublicLayout = lazy(() =>
  import('./components/public/PublicLayout').then((module) => ({
    default: module.PublicLayout,
  }))
);
const Home = lazy(() =>
  import('./pages/backoffice/Home').then((module) => ({ default: module.Home }))
);
const Galleries = lazy(() =>
  import('./pages/backoffice/galleries/Galleries').then((module) => ({
    default: module.Galleries,
  }))
);
const Login = lazy(() =>
  import('./pages/backoffice/Login').then((module) => ({
    default: module.Login,
  }))
);

const Clients = lazy(() =>
  import('./pages/backoffice/clients/Clients').then((module) => ({
    default: module.Clients,
  }))
);
const Scheduling = lazy(() =>
  import('./pages/backoffice/Scheduling').then((module) => ({
    default: module.Scheduling,
  }))
);
const Settings = lazy(() =>
  import('./pages/backoffice/Settings').then((module) => ({
    default: module.Settings,
  }))
);
const Documents = lazy(() =>
  import('./pages/backoffice/Documents').then((module) => ({
    default: module.Documents,
  }))
);
const Analytics = lazy(() =>
  import('./pages/backoffice/Analytics').then((module) => ({
    default: module.Analytics,
  }))
);
const Landing = lazy(() =>
  import('./pages/landing/Landing').then((module) => ({ default: module.Landing }))
);
const GalleryInvite = lazy(() =>
  import('./pages/public/GalleryInvite').then((module) => ({
    default: module.GalleryInvite,
  }))
);
const CustomerGallery = lazy(() =>
  import('./pages/public/CustomerGallery').then((module) => ({
    default: module.CustomerGallery,
  }))
);
const CustomPage = lazy(() =>
  import('./pages/public/CustomPage').then((module) => ({
    default: module.CustomPage,
  }))
);
const GalleryPage = lazy(() =>
  import('./pages/backoffice/galleries/GalleryPage').then((module) => ({
    default: module.GalleryPage,
  }))
);
const NewGalleryPage = lazy(() =>
  import('./pages/backoffice/galleries/NewGalleryPage').then((module) => ({
    default: module.NewGalleryPage,
  }))
);

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
  </div>
);

// Root component to handle layout selection
function RootLayout() {
  const usePublicLayout =
    document.querySelector('meta[name="x-use-public-layout"]')?.getAttribute('content') === 'true';
  const photographerSlug = document
    .querySelector('meta[name="x-photographer-slug"]')
    ?.getAttribute('content');

  // If we have a custom domain or photographer slug, use the public layout
  if (usePublicLayout || photographerSlug) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <PublicErrorBoundary>
          <PublicLayout />
        </PublicErrorBoundary>
      </Suspense>
    );
  }

  // Otherwise show the landing page
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <PublicErrorBoundary>
        <Landing />
      </PublicErrorBoundary>
    </Suspense>
  );
}

// Define routes
const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PublicErrorBoundary>
              <CustomPage />
            </PublicErrorBoundary>
          </Suspense>
        ),
      },
      {
        path: 'login',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PublicErrorBoundary>
              <Login />
            </PublicErrorBoundary>
          </Suspense>
        ),
      },
      {
        path: 'invite/:galleryId',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PublicErrorBoundary>
              <GalleryInvite />
            </PublicErrorBoundary>
          </Suspense>
        ),
      },
      {
        path: 'gallery/:galleryId',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PublicErrorBoundary>
              <CustomerGallery />
            </PublicErrorBoundary>
          </Suspense>
        ),
      },
    ],
  },
  // Slug-based routes
  {
    path: '/p/:photographerSlug',
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <PublicErrorBoundary>
          <PublicLayout />
        </PublicErrorBoundary>
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PublicErrorBoundary>
              <CustomPage />
            </PublicErrorBoundary>
          </Suspense>
        ),
      },
      {
        path: 'invite/:galleryId',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PublicErrorBoundary>
              <GalleryInvite />
            </PublicErrorBoundary>
          </Suspense>
        ),
      },
      {
        path: 'gallery/:galleryId',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PublicErrorBoundary>
              <CustomerGallery />
            </PublicErrorBoundary>
          </Suspense>
        ),
      },
      {
        // Add wildcard route to catch all other paths
        path: '*',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PublicErrorBoundary>
              <CustomPage />
            </PublicErrorBoundary>
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/app',
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <ProtectedRoute>
          <BackofficeErrorBoundary>
            <ThemeProvider>
              <BackofficeLayout />
            </ThemeProvider>
          </BackofficeErrorBoundary>
        </ProtectedRoute>
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Home />
          </Suspense>
        ),
      },
      {
        path: 'galleries',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <Galleries />
              </Suspense>
            ),
          },
          {
            path: 'new',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <NewGalleryPage />
              </Suspense>
            ),
          },
          {
            path: ':galleryId',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <GalleryPage />
              </Suspense>
            ),
          },
          {
            path: ':galleryId/edit',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <GalleryPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'scheduling',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Scheduling />
          </Suspense>
        ),
      },
      {
        path: 'clients',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <Clients />
              </Suspense>
            ),
          },
          {
            path: 'customers',
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <Clients />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'documents',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Documents />
          </Suspense>
        ),
      },
      {
        path: 'analytics',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Analytics />
          </Suspense>
        ),
      },
      {
        path: 'settings',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Settings />
          </Suspense>
        ),
      },
    ],
  },
]);

export function Router() {
  return <RouterProvider router={router} />;
}
